/* eslint-disable no-unused-vars */
import { observer } from 'mobx-react';
import { MaterialReactTable } from 'material-react-table';
import { useEffect, useMemo, useState, forwardRef } from 'react';
import { Box } from '@mui/material';
import convertSchemaToColumns from './lib/convertSchemaToColumns';
import { useLocalization } from '../../providers/LocalizationProvider';
import { useStore } from '../../providers/MobxProvider';
import LoggerUtil from '../../helpers/LoggerUtil';

/*
 * Pagination resets because rowcount is calculated as 0 on initial load
 */

const FormList2 = forwardRef(
  (
    {
      columnPrimaryId,
      schema,
      dataProvider,
      initialState,
      listApiUrl,
      accessorFunctions = {},
      onRowCountChange = () => undefined,
      accessorFunctionsAdditional = {},
      children = null,
      onApiResponse = () => undefined,
      stickyColumn = false,
      storeName = null,
      responseFormatter = null,
      sortBy,
      sortOrder,
      rowsPerPageOptions = [5, 10, 15, 20, 25, 30, 50],
    },
    ref,
  ) => {
    const { formatMessage } = useLocalization();
    const store = useStore();

    if (!storeName) {
      throw new Error('Store name is needed as list prop');
    }

    const {
      paginationState,
      filters = {},
      sortingState = {},
    } = store.domain.list.get(storeName);

    const { get } = dataProvider;

    // data and fetching state
    const [tableData, setTableData] = useState([]);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefetching, setIsRefetching] = useState(false);

    // table state
    const [rowCount, setRowCount] = useState(0);

    const columns = useMemo(() =>
      convertSchemaToColumns(
        schema,
        formatMessage,
        accessorFunctions,
        accessorFunctionsAdditional,
        stickyColumn,
      ),
    );

    async function fetchData() {
      if (!tableData.length) {
        setIsLoading(true);
      } else {
        setIsRefetching(true);
      }

      const apiUrl = new URL(listApiUrl);

      apiUrl.searchParams.set('page', `${paginationState.pageIndex + 1 || 1}`);
      apiUrl.searchParams.set('limit', `${paginationState?.pageSize || 10}`);

      // if (sortingState[0]?.id) {
      //   apiUrl.searchParams.set('sortBy', `${sortingState?.[0]?.id || sortBy}`);
      //   apiUrl.searchParams.set(
      //     'sortOrder',
      //     `${sortingState[0].desc ? 'DESC' : 'ASC'}`,
      //   );
      // }

      if (filters && Object.keys(filters).length > 0) {
        Object.entries(filters).forEach(([key, value]) => {
          apiUrl.searchParams.set(key, value);
        });
      }

      try {
        const apiResponse = await get(apiUrl.href);
        onApiResponse(apiResponse);
        if (apiResponse?.ok) {
          const response = apiResponse.data;
          let tableDataResponse = response.data;
          if (responseFormatter) {
            tableDataResponse = responseFormatter(tableDataResponse);
          }
          setTableData(tableDataResponse);
          const tempRowCount =
            response?.total || response?.totalItems || response?.data.length;
          setRowCount(tempRowCount);
        }
      } catch (error) {
        setIsError(true);
        LoggerUtil.log('List API Error', error);
      }
      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    }

    useEffect(() => {
      fetchData();
    }, [
      JSON.stringify(paginationState),
      JSON.stringify(filters),
      JSON.stringify(sortingState),
    ]);

    useEffect(() => {
      onRowCountChange(rowCount);
    }, [rowCount]);

    return (
      <Box ref={ref}>
        <MaterialReactTable
          autoResetPageIndex={false}
          localization={store.domain.locale.getForMuiTable()}
          columns={columns}
          data={tableData}
          getRowId={row => row[columnPrimaryId]}
          rowCount={rowCount}
          manualFiltering
          enableTopToolbar={false}
          enableBottomToolbar
          manualPagination
          manualSorting={false}
          muiPaginationProps={{
            ...(rowsPerPageOptions !== null && { rowsPerPageOptions }),
            disabled: isLoading || isRefetching,
          }}
          onPaginationChange={updater => {
            // TODO: remove after version upgrade
            const newPagination =
              typeof updater === 'function'
                ? updater(paginationState)
                : updater;

            store.domain.list.set(storeName, {
              paginationState: newPagination,
              sortingState,
            });
          }}
          onSortingChange={updater => {
            // TODO: remove after version upgrade
            const newSorting =
              typeof updater === 'function' ? updater(sortingState) : updater;

            store.domain.list.set(storeName, {
              paginationState,
              sortingState: newSorting,
            });
          }}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableSorting={false}
          enableFilters={false}
          initialState={{
            ...initialState,
            pagination: paginationState,
          }}
          state={{
            globalFilter: '',
            isLoading,
            showAlertBanner: isError,
            showProgressBars: isRefetching,
            sorting: sortingState,
            pagination: paginationState,
          }}
          muiToolbarAlertBannerProps={
            isError
              ? {
                  color: 'error',
                  children: formatMessage({
                    id: 'ERROR_LOADING_DATA',
                    defaultMessage: 'Error loading data',
                  }),
                }
              : undefined
          }
          muiTablePaperProps={{ elevation: 0 }}
          muiBottomToolbarProps={{
            sx: {
              minHeight: '2.5em',
            },
          }}
        />
        {children}
      </Box>
    );
  },
);

export default observer(FormList2);
